import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subject, Subscription, switchMap } from 'rxjs';
import { ProductService } from 'src/app/core/api/product/product.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { BusinessStatus } from 'src/app/shared/models/constants/business-status';
import { DetailedRegion } from 'src/app/shared/models/dtos/region/detailed-region';
import { Region } from 'src/app/shared/models/dtos/region/region';
import { BusinessCard } from 'src/app/shared/models/dtos/responses/business-card/business-card';
import { Country } from 'src/app/shared/models/dtos/responses/country/country';
import { FilterOptions } from 'src/app/shared/models/filters/filter-options';
import { dedup } from 'src/app/utils/list';
import { Regions } from 'src/app/utils/regions';
import {
  generateSeriesChips,
  MultiSelectChip,
} from '../filter-container/filter-container.component';

@Component({
  selector: 'app-businesses-feed',
  templateUrl: './businesses-feed.component.html',
  styleUrls: ['./businesses-feed.component.scss'],
})

/**
 * Businesses list component
 */
export class BusinessesFeedComponent implements OnInit, OnDestroy {
  userRegionFormControl = new FormControl();
  feedItems: BusinessCard[] = [];
  subregions: Region[] = [];
  showFilter: boolean = false;
  countriesFromRegion: Country[] = [];

  vipChip = ChipContent.Vip();
  statusChips = ChipContent.Status();
  seriesChips: MultiSelectChip[] = [];
  chipContents: ChipContent[] = [this.vipChip];
  filters: FilterOptions = {
    orderAsc: true,
    status: [],
    vip: false,
    series: [],
    countries: [],
  };
  page = 0;
  pageSize = 25;
  loading = false;
  pullingBusiness = false;
  search = new FormControl('', [Validators.minLength(3)]);
  currentSearch?: string | null;
  snackBarLabels = {
    error: 'unexpected-error',
  };

  private readonly subsink = new Subscription();
  private businesses$ = new Subject<{ reset: boolean }>();
  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private productService: ProductService,
    private readonly snackbarService: SnackBarService,
    private readonly translationService: TranslationService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly cdr: ChangeDetectorRef
  ) {}
  /**
   * on init
   */
  ngOnInit() {
    this.translationService.translateObject(this.snackBarLabels);

    this.productService.getAllFamilyProduct().subscribe({
      next: (productSeriesList) => {
        this.seriesChips = generateSeriesChips(productSeriesList);
      },
    });

    this.subsink.add(
      this.businesses$
        .pipe(
          switchMap(({ reset }) => {
            if (reset) {
              this.page = 0;
              this.feedItems = [];
            }
            const params = this.prepareParams();
            if (!params) {
              return [];
            }
            return this.userService.getBusinesses(params);
          })
        )
        .subscribe({
          next: (moreBusinesses) => {
            if (moreBusinesses.length > 0) {
              this.feedItems.push(...moreBusinesses);
              this.feedItems = dedup(this.feedItems, (item) => item.businessId);
              if (moreBusinesses.length === this.pageSize) {
                this.page++;
              }
            }
            this.loading = false;
          },
          error: () => {
            this.snackbarService.error(this.snackBarLabels.error);
            this.loading = false;
          },
        })
    );
    this.subsink.add(
      this.authenticatedUserService.currentRegion$.subscribe((region) => {
        this.userRegionFormControl.setValue(region ?? null);
        this.fetchSubregions();
      })
    );
  }

  /**
   * Cleans up subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Updates Filters
   * @param {AdminFilters} filters
   */
  updateFilter(filters: FilterOptions) {
    this.filters = filters;
    this.getBusinesses(true);
  }

  /**
   * GetBusinesses
   * @param {boolean} search
   */
  getBusinesses(search?: boolean) {
    this.loading = true;
    if (search) {
      this.currentSearch = this.search.value;
    }
    this.businesses$.next({ reset: !!search });
  }

  /**
   * Fetches the subregions from the available regions
   */
  fetchSubregions() {
    this.userService.listCountries().subscribe({
      next: (regions) => {
        const selectedRegion = regions.find(
          (region) => this.userRegionFormControl.value?.id === region.id
        );

        if (selectedRegion) {
          this.countriesFromRegion = selectedRegion.countries.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          this.subregions = [
            ...this.extractSubregionFromRegion(selectedRegion),
          ].sort((a, b) => a.name.localeCompare(b.name));
        } else {
          this.subregions = [];
        }
      },
      error: () => {
        this.loading = false;
        this.snackbarService.error(this.snackBarLabels.error);
      },
      complete: () => {
        this.showFilter = true;
      },
    });
  }

  /**
   * Update the country filter values and calls the method to retrieve business
   * @param {string[]} selectedValues
   */
  onCountryFilterChanged(selectedValues: string[]) {
    this.filters.countries = selectedValues;
    this.getBusinesses(true);
  }

  /**
   * Prepares search params for business list page
   * @return {Object}
   */
  private prepareParams() {
    const regionValue = this.userRegionFormControl.value;
    if (!regionValue?.id) return;

    let params = {
      page: this.page,
      pageSize: this.pageSize,
      orderAsc: this.filters.orderAsc,
      vip: this.filters.vip,
      regionId: regionValue.id,
      series: this.filters.series,
      countries: this.filters.countries,
      subregionId: this.filters.subregions,
      status: this.filters.status,
      search: this.search.getRawValue()!,
    };
    if (this.filters.status?.length === 0) {
      params = {
        ...params,
        status: [
          BusinessStatus.ACTIVE,
          BusinessStatus.DISABLED,
          BusinessStatus.IDLE,
          BusinessStatus.INVITED,
        ],
      };
    }
    return params;
  }

  /**
   * retrieves the subregions for a region
   * @param {DetailedRegion} region
   * @return {Region[]}
   */
  private extractSubregionFromRegion(region: DetailedRegion): Region[] {
    if (!region || !region.countries) return [];
    const countryRegions = [
      Regions.BRAZIL.valueOf(),
      Regions.COLOMBIA.valueOf(),
      Regions.MEXICO.valueOf(),
    ];
    if (countryRegions.find((countryRegion) => countryRegion === region.id)) {
      const country = region.countries[0];
      return country?.provinces ?? [];
    } else {
      return region.countries;
    }
  }
}
